import { useModal } from '@billy/design-system'
import { createContext, ReactElement, ReactNode, useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import { FileRejection } from 'react-dropzone'

import { useUserOrganizationSettings } from '@modules-deprecated/app/organization'

import { ModalId } from '../../../enums/modalId'
import { ImportedInvoicesUploadInfoModal } from '../elements/ImportedInvoicesUploadInfoModal'
import { useUploadImportedInvoices } from '../hooks/useUploadImportedInvoices'

interface UploadProgress {
  current: number
  total: number
}

interface ImportedInvoicesUploadContextValue {
  isUploadBlocked: boolean
  isUploading: boolean
  progress: UploadProgress
  uploadFiles: (filesAccepted: File[], filesRejected?: FileRejection[]) => void
}

interface PendingUpload {
  filesAccepted: File[]
  filesRejected?: FileRejection[]
}

const ImportedInvoicesUploadContext = createContext<ImportedInvoicesUploadContextValue | undefined>(undefined)

interface ImportedInvoicesUploadProviderProps {
  children?: ReactNode
}

/**
 * The upload button is mounted twice (page header + empty-state CTA) alongside the
 * dropzone. Sharing a single `useUploadImportedInvoices()` instance keeps their
 * `isUploading`/`progress` in sync so the buttons reflect the dropzone's activity
 * (e.g. `forceActive`) instead of each holding its own detached state.
 *
 * STR-225 / S25 — because all three entry points funnel through this one `uploadFiles`,
 * the pre-upload warning dialog is gated here rather than per call site. Gating the
 * mutation itself (in `useUploadImportedInvoices`) would flip `isUploading` — and with it
 * the upload button's loading spinner — while the dialog is still open and nothing has
 * been uploaded.
 */
export const ImportedInvoicesUploadProvider = ({ children }: ImportedInvoicesUploadProviderProps): ReactElement => {
  const { isUploading, progress, uploadFiles } = useUploadImportedInvoices()
  const {
    isLoading: isOrganizationSettingsLoading,
    isPreviousData: isOrganizationSettingsPreviousData,
    organizationSettings,
  } = useUserOrganizationSettings()
  const {
    close: closeUploadInfoModal,
    isOpen: isUploadInfoModalOpen,
    open: openUploadInfoModal,
  } = useModal(ModalId.ImportedInvoicesUploadInfoModal)
  const pendingUploadRef = useRef<PendingUpload | null>(null)

  const areOrganizationSettingsReady = !isOrganizationSettingsLoading && !isOrganizationSettingsPreviousData

  // The `!areOrganizationSettingsReady` term makes the dialog show rather than be silently
  // skipped while the query is in flight or still serving another organization's data.
  const shouldGateUpload =
    !areOrganizationSettingsReady || organizationSettings?.isImportedInvoicesUploadInfoModalDismissed !== '1'

  const gatedUploadFiles = useCallback(
    (filesAccepted: File[], filesRejected?: FileRejection[]) => {
      // Checked before `shouldGateUpload` so a selection held by the open dialog stays held even
      // if the settings query resolves to "dismissed" in the meantime. Blocking hides the
      // dropzone and disables the upload button, but the hidden file input is still in the DOM
      // behind the modal. The held selection wins, but a rejected second selection still gets
      // its toast — dropping it silently would be a regression on today's behaviour.
      if (pendingUploadRef.current) {
        if (filesRejected?.length) {
          uploadFiles([], filesRejected)
        }

        return
      }

      // Nothing to warn about when the whole selection was rejected — pass it straight
      // through so the rejection toasts still fire.
      if (filesAccepted.length === 0 || !shouldGateUpload) {
        uploadFiles(filesAccepted, filesRejected)

        return
      }

      pendingUploadRef.current = { filesAccepted, filesRejected }
      openUploadInfoModal()
    },
    [openUploadInfoModal, shouldGateUpload, uploadFiles],
  )

  const handleModalAccept = useCallback(() => {
    const pendingUpload = pendingUploadRef.current
    // Cleared before firing, so the held selection cannot be uploaded twice even if
    // `uploadFiles` throws.
    pendingUploadRef.current = null

    if (pendingUpload) {
      uploadFiles(pendingUpload.filesAccepted, pendingUpload.filesRejected)
    }
  }, [uploadFiles])

  const handleModalDismiss = useCallback(() => {
    const pendingUpload = pendingUploadRef.current
    pendingUploadRef.current = null

    // Cancelling drops the accepted files but not the reason the rejected ones failed:
    // replaying with an empty accepted list runs the mutation's rejection notifications
    // and nothing else. Note this also changes which rejection toast the user sees — with
    // no accepted files a lone oversize rejection takes the filename-less sentence.
    if (pendingUpload?.filesRejected?.length) {
      uploadFiles([], pendingUpload.filesRejected)
    }
  }, [uploadFiles])

  const value = useMemo(
    () => ({
      // `DropzoneFullScreen` portals above the modal in the shared z-index order, so a drop
      // would otherwise land on top of the open dialog and overwrite the held selection.
      isUploadBlocked: isUploadInfoModalOpen,
      isUploading,
      progress,
      uploadFiles: gatedUploadFiles,
    }),
    [gatedUploadFiles, isUploadInfoModalOpen, isUploading, progress],
  )

  // `ModalContextProvider` sits above the routes, and the modal reducer has no route awareness,
  // so an open dialog outlives this view: coming back would re-render it against an empty
  // `pendingUploadRef`, with both upload entry points blocked and "Upload" doing nothing.
  useEffect(
    () => () => {
      // React fires deletion cleanups parent-to-child, so the modal's own `onClose` subscriber is
      // still registered here and closing it will run `onDismiss`. Clearing the ref first is what
      // makes that a no-op instead of an upload mutation firing from an unmounting tree.
      pendingUploadRef.current = null
      closeUploadInfoModal()
    },
    [closeUploadInfoModal],
  )

  return (
    <ImportedInvoicesUploadContext.Provider value={value}>
      {children}
      <ImportedInvoicesUploadInfoModal
        id={ModalId.ImportedInvoicesUploadInfoModal}
        onAccept={handleModalAccept}
        onDismiss={handleModalDismiss}
      />
    </ImportedInvoicesUploadContext.Provider>
  )
}

export const useImportedInvoicesUploadContext = (): ImportedInvoicesUploadContextValue => {
  const context = useContext(ImportedInvoicesUploadContext)

  if (!context) {
    throw new Error('ImportedInvoicesUploadContext is missing in the module!')
  }

  return context
}
